import { ClientApplicationForTableFragment } from "@graphql/client-applications/ClientApplications.g";
import {
  TenantedClientApplicationManagedByChoices,
  TenantedClientApplicationReviewStateChoices,
  TenantedClientApplicationSupportedByChoices,
} from "@graphql/generated/graphql.g";
import React from "react";

import { ClientApplicationFilterFormValues } from "@components/Forms/ClientApplicationFilterForm/ClientApplicationFilterForm";
import {
  ManagedByTag,
  ReviewStateTag,
  SupportedByTag,
} from "@components/Texts";

import { Box } from "@design-system/Box/Box";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Table } from "@design-system/Table/Table";
import { Tag } from "@design-system/Tag/Tag";
import { Text } from "@design-system/Text/Text";

import { createTableConfiguration } from "@utils/table/TableConfigurationSystem";

import {
  CLIENT_APPLICATION_TABLE_COLUMN_LABELS,
  ClientApplicationTableColumns,
  DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_SETTINGS,
} from "./UI/ClientApplicationsTableColumns";

/**
 * Default filter values for Client Applications
 */
export const DEFAULT_CLIENT_APPLICATION_FILTER_VALUES: ClientApplicationFilterFormValues =
  {
    reviewState: undefined,
    managedBy: undefined,
    businessOwnerId: undefined,
    supportedBy: undefined,
    productNameSearch: undefined,
    parentCategoryId: undefined,
    createdAt_Gte: undefined,
    createdAt_Lte: undefined,
    // Default "apps in use" list omits hidden apps (AF-534 / AF-533).
    isVisibleFilter: "visible",
  };

/**
 * Comprehensive table configuration for Client Applications
 */
export const ClientApplicationsTableConfig = createTableConfiguration<
  ClientApplicationForTableFragment,
  ClientApplicationTableColumns
>({
  tableId: "client-applications",
  columns: {
    [ClientApplicationTableColumns.productName]: {
      id: ClientApplicationTableColumns.productName,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.productName
        ],
      fieldPath: "resource.productName",
      sortField: "resource__product_name",
      type: "custom",
      defaultVisible: true,
    },
    [ClientApplicationTableColumns.isVisible]: {
      id: ClientApplicationTableColumns.isVisible,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.isVisible
        ],
      fieldPath: "resource.isVisible",
      sortField: "resource__is_visible",
      type: "custom",
      defaultVisible: true,
    },
    [ClientApplicationTableColumns.reviewState]: {
      id: ClientApplicationTableColumns.reviewState,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.reviewState
        ],
      fieldPath: "reviewState",
      sortField: "review_state",
      type: "custom",
      defaultVisible: true,
    },
    [ClientApplicationTableColumns.managedBy]: {
      id: ClientApplicationTableColumns.managedBy,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.managedBy
        ],
      fieldPath: "managedBy",
      sortField: "managed_by",
      type: "custom",
      defaultVisible: true,
    },
    [ClientApplicationTableColumns.supportedBy]: {
      id: ClientApplicationTableColumns.supportedBy,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.supportedBy
        ],
      fieldPath: "supportedBy",
      sortField: "supported_by",
      type: "custom",
      defaultVisible: true,
    },
    [ClientApplicationTableColumns.usedBy]: {
      id: ClientApplicationTableColumns.usedBy,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.usedBy
        ],
      fieldPath: "resourceGroupApplications.stats.totalEmployees",
      sortField: "rg_total_employees",
      type: "count",
      defaultVisible: true,
    },
    [ClientApplicationTableColumns.category]: {
      id: ClientApplicationTableColumns.category,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.category
        ],
      fieldPath: "resource.publicResource.category.parent.name",
      sortable: false,
      type: "custom",
      defaultVisible: true,
    },
    [ClientApplicationTableColumns.vendorName]: {
      id: ClientApplicationTableColumns.vendorName,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.vendorName
        ],
      fieldPath: "resource.vendorName",
      sortField: "resource__vendor_name",
      type: "text",
      defaultVisible: false,
    },
    [ClientApplicationTableColumns.businessOwner]: {
      id: ClientApplicationTableColumns.businessOwner,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.businessOwner
        ],
      fieldPath: "businessOwner.name",
      sortField: "business_owner_id",
      sortable: false,
      type: "text",
      defaultVisible: false,
    },
    [ClientApplicationTableColumns.createdAt]: {
      id: ClientApplicationTableColumns.createdAt,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.createdAt
        ],
      fieldPath: "createdAt",
      sortField: "created_at",
      sortable: true,
      type: "date",
      defaultVisible: true,
    },
    [ClientApplicationTableColumns.lastUsed]: {
      id: ClientApplicationTableColumns.lastUsed,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.lastUsed
        ],
      fieldPath: "resourceGroupApplications.stats.lastSessionTime",
      sortField: "tracking_sessions__max__updated_at",
      sortable: true,
      type: "date",
      defaultVisible: true,
    },
    [ClientApplicationTableColumns.updatedAt]: {
      id: ClientApplicationTableColumns.updatedAt,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.updatedAt
        ],
      fieldPath: "updatedAt",
      sortField: "updated_at",
      type: "date",
      defaultVisible: false,
    },
    [ClientApplicationTableColumns.notes]: {
      id: ClientApplicationTableColumns.notes,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.notes
        ],
      fieldPath: "notes",
      sortable: false,
      type: "text",
      defaultVisible: false,
    },
    [ClientApplicationTableColumns.notifications]: {
      id: ClientApplicationTableColumns.notifications,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.notifications
        ],
      fieldPath: "resourceGroupApplications.stats.totalNotifications",
      sortField: "notifications__count",
      type: "count",
      defaultVisible: false,
    },
    [ClientApplicationTableColumns.productDescription]: {
      id: ClientApplicationTableColumns.productDescription,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.productDescription
        ],
      fieldPath: "resource.productDescription",
      sortable: false,
      type: "text",
      defaultVisible: false,
    },
    [ClientApplicationTableColumns.vendorDescription]: {
      id: ClientApplicationTableColumns.vendorDescription,
      label:
        CLIENT_APPLICATION_TABLE_COLUMN_LABELS[
          ClientApplicationTableColumns.vendorDescription
        ],
      fieldPath: "resource.vendorDescription",
      sortable: false,
      type: "text",
      defaultVisible: false,
    },
  },
  defaultColumnOrder: DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_ORDER,
  defaultColumnSettings: DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_SETTINGS,
});

/**
 * Helper function to get default filter values
 */
export const getDefaultFilterValues =
  (): ClientApplicationFilterFormValues => ({
    ...DEFAULT_CLIENT_APPLICATION_FILTER_VALUES,
  });

/**
 * Helper function to check if filters are active
 */
export const areFiltersActive = (
  filterValues: ClientApplicationFilterFormValues,
): boolean => {
  return Boolean(
    (filterValues.reviewState && filterValues.reviewState.length > 0) ||
      filterValues.managedBy ||
      filterValues.businessOwnerId ||
      filterValues.supportedBy ||
      filterValues.productNameSearch ||
      (filterValues.parentCategoryId &&
        filterValues.parentCategoryId.trim() !== "") ||
      filterValues.createdAt_Gte ||
      filterValues.createdAt_Lte ||
      (filterValues.isVisibleFilter &&
        filterValues.isVisibleFilter !== "visible"),
  );
};

/**
 * Custom render functions for specific columns
 */
export function renderProductName(
  value: unknown,
  row: ClientApplicationForTableFragment,
  onApplicationDashboardClick: (id: string) => void,
): React.ReactNode {
  const isHidden = row.resource?.isVisible === false;

  return (
    <Clickable
      onClick={() => onApplicationDashboardClick(row.id)}
      outline="sm"
      stretch
    >
      <Box direction="row" align="center" gap={8}>
        <Table.CompanyCell
          name={row.resource?.vendorName ?? ""}
          subtext={(value as string) || undefined}
          signedLogoUrl={
            row.resource?.publicResource?.signedLogoUrl || undefined
          }
          domain={
            row.resource?.vendorDomainName ??
            row.resource?.productDomainName ??
            ""
          }
        />
        {isHidden && <Tag color="secondary" text="Hidden" />}
      </Box>
    </Clickable>
  );
}

export function renderIsVisible(value: unknown): React.ReactNode {
  const isVisible = value as boolean;
  return (
    <Tag
      color={isVisible ? "success" : "secondary"}
      text={isVisible ? "Visible" : "Hidden"}
    />
  );
}

export function renderReviewState(value: unknown): React.ReactNode {
  return value ? (
    <ReviewStateTag
      text={value as TenantedClientApplicationReviewStateChoices}
    />
  ) : null;
}

export function renderManagedBy(value: unknown): React.ReactNode {
  return value ? (
    <ManagedByTag text={value as TenantedClientApplicationManagedByChoices} />
  ) : null;
}

export function renderSupportedBy(value: unknown): React.ReactNode {
  return value ? (
    <SupportedByTag
      text={value as TenantedClientApplicationSupportedByChoices}
    />
  ) : null;
}

export function renderUsedBy(
  value: unknown,
  row: ClientApplicationForTableFragment,
  onUsersClick: (applicationId: string) => void,
): React.ReactNode {
  return (
    <Clickable onClick={() => onUsersClick(row.id)} outline="sm" stretch>
      <Text>{String(value)}</Text>
    </Clickable>
  );
}

export function renderCategory(value: unknown): React.ReactNode {
  const displayName = value || "—";
  return (
    <Box>
      <Tag color="secondary" text={String(displayName)} />
    </Box>
  );
}

export function renderVendorName(value: unknown): React.ReactNode {
  return <Text>{String(value)}</Text>;
}

export function renderBusinessOwner(value: unknown): React.ReactNode {
  return <Text>{value ? String(value) : "—"}</Text>;
}

export function renderCreatedAt(value: unknown): React.ReactNode {
  if (!value) {
    return <Text size={14}>—</Text>;
  }

  return <Table.DateCell date={new Date(value as string)} />;
}

export function renderLastUsed(value: unknown): React.ReactNode {
  if (!value) {
    return <Text size={14}>—</Text>;
  }

  return <Table.DateCell date={new Date(value as string)} />;
}

export function renderUpdatedAt(value: unknown): React.ReactNode {
  if (!value) {
    return <Text size={14}>—</Text>;
  }

  return <Table.DateCell date={new Date(value as string)} />;
}

export function renderNotes(value: unknown): React.ReactNode {
  return <Text>{String(value) || "-"}</Text>;
}

export function renderNotifications(value: unknown): React.ReactNode {
  return <Text>{String(value)}</Text>;
}

export function renderProductDescription(value: unknown): React.ReactNode {
  return (
    <Text truncate maxWidth={200} title={String(value) || undefined}>
      {String(value) || "-"}
    </Text>
  );
}

export function renderVendorDescription(value: unknown): React.ReactNode {
  return (
    <Text truncate maxWidth={200} title={String(value) || undefined}>
      {String(value) || "-"}
    </Text>
  );
}
